import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { createCheckoutSession, formatApiErrorDetail } from "@/lib/api";
import { track, getUTM } from "@/lib/analytics";

const labelClass = "block text-[11px] uppercase tracking-[0.14em] font-normal text-[color:var(--grey)]";

// Generic Stripe checkout dialog: collects name + email, then redirects.
export function CheckoutDialog({ open, onOpenChange, productKey, eventSlug, title, priceLabel, kicker }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const data = await createCheckoutSession({
        product_key: productKey,
        event_slug: eventSlug || null,
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
        utm: getUTM(),
      });
      track("door_click", { target: "stripe_checkout", product: productKey, event: eventSlug });
      window.location.href = data.url;
    } catch (err) {
      setBusy(false);
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not start checkout.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-dialog" className="exec-scope bg-[color:var(--paper)] border-[color:var(--rule)] text-[color:var(--ink)] sm:max-w-md">
        <DialogHeader>
          <p className="exec-eyebrow">{kicker || "Secure checkout"}</p>
          <DialogTitle className="font-light text-[clamp(22px,2.4vw,28px)] leading-[1.18] mt-2">{title}</DialogTitle>
          <DialogDescription className="exec-serif text-[15.5px] text-[color:var(--lead-ink)]">
            {priceLabel ? `${priceLabel}. ` : ""}Your email is verified before payment. Checkout by Stripe.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <label className="block">
            <span className={labelClass}>Name</span>
            <input data-testid="checkout-dialog-name" className="exec-underline mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="block">
            <span className={labelClass}>Email</span>
            <input data-testid="checkout-dialog-email" type="email" className="exec-underline mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          {error && <p data-testid="checkout-dialog-error" className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3">{error}</p>}
          <button data-testid="checkout-dialog-submit" type="submit" disabled={busy} className="exec-btn exec-btn-primary w-full disabled:opacity-60">
            {busy ? "Opening checkout\u2026" : "Continue to payment"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
